import { countsAsDayVisit } from './day-visits'
import { isPencilledVisit } from './planning'
import { getMe } from './me'
import { el, money } from './ui'
import { styleSelect } from './styled-select'
import { rowMenu } from './row-menu'
import { isContact, isSuggestion } from './planning'
import type { Clean } from './types'
import './diary-new.css'
import { takeLanding } from './diary-land'

export function diaryPreferenceKey(name: string): string { return `roundly:diary-${name}:${identity() ?? 'unsigned'}` }
function identity(): string | null {
  const me = getMe()
  return me?.role === 'owner' && me.email && me.businessId ? `${me.businessId}:${me.email}` : null
}
// The one diary (tidy-up item 4; Liam, 20 Oct 2026: Original goes for good). The Diary's page carries `diary-new`, as it
// did while New was a choice, and its top line carries `diary-top`: the New diary's styles key on the pair
// (diary-new.css, `.diary-new:has(.diary-top)`), as they keyed on the switch, so they hold only while the Diary is drawn.
// A phone that had chosen Original forgets it here.
const forgotten = new Set<string>()
export function diaryPage(root: HTMLElement, top: HTMLElement): void {
  root.classList.add('diary-new')
  top.classList.add('diary-top')
  const who = identity()
  if (!who || forgotten.has(who)) return
  forgotten.add(who)
  try { localStorage.removeItem(`roundly:diary-layout:${who}`) } catch { /* Nothing kept where storage is refused. */ }
}
let observedTopbar: Element | null = null
let topbarObserver: ResizeObserver | null = null
let stickyObserver: ResizeObserver | null = null
// The day someone was just put on lights up once when the Diary is next drawn (the movement set's land, 450ms; Liam,
// 24 Oct 2026, "where else in the app", 4): its date in the strip, its card or row in the week, its cell in Month.
export { diaryLandOn } from './diary-land'
export function finishDiary(root: HTMLElement): void {
  const date = takeLanding()
  if (date) {
    for (const spot of root.querySelectorAll<HTMLElement>(`.diary-week-date > button[data-date="${date}"], .diary-strip-day[data-date="${date}"], .day-card[data-date="${date}"]:not([hidden]), .month-day[data-date="${date}"], .diary-phone-week-row[data-date="${date}"]`)) {
      spot.classList.remove('rd-land'); void spot.offsetWidth; spot.classList.add('rd-land')
      window.setTimeout(() => spot.classList.remove('rd-land'), 600)
    }
  }
  stickyObserver?.disconnect(); stickyObserver = null
  for (const select of root.querySelectorAll<HTMLSelectElement>('select')) styleSelect(select)
  if (completionSettle) {
    const landing = completionSettle; completionSettle = null
    if (landing.current() && landing.root === root && landing.who === diaryPreferenceKey('completion')) {
      const row = root.querySelector<HTMLElement>(`.day-card [data-clean-id="${landing.id}"]`)
      if (row) diaryMotion(row,[{transform:`translateY(${Math.max(-240,Math.min(240,landing.top-row.getBoundingClientRect().top))}px)`,opacity:.6},{transform:'translateY(0)',opacity:1}],250)
    }
  }
  // These bars can wrap at narrow widths; date jumps must clear their actual stack.
  const strip = root.querySelector<HTMLElement>('.diary-week-strip')
  const moveBar = root.querySelector<HTMLElement>('.diary-move-bar')
  const measureSticky = (): void => {
    if (!root.isConnected) { stickyObserver?.disconnect(); return }
    root.style.setProperty('--diary-week-strip-height', `${strip?.getBoundingClientRect().height ?? 0}px`)
    root.style.setProperty('--diary-move-bar-height', `${moveBar?.getBoundingClientRect().height ?? 0}px`)
  }
  measureSticky()
  if (typeof ResizeObserver === 'function' && (strip || moveBar)) {
    const observer = new ResizeObserver(() => { if (stickyObserver === observer) measureSticky() })
    stickyObserver = observer
    if (strip) stickyObserver.observe(strip)
    if (moveBar) stickyObserver.observe(moveBar)
  }
  const topbar = document.querySelector<HTMLElement>('.topbar')
  if (topbar && topbar !== observedTopbar) {
    observedTopbar = topbar; topbarObserver?.disconnect()
    const measure = (): void => document.documentElement.style.setProperty('--diary-topbar-height', `${topbar.getBoundingClientRect().height}px`)
    measure()
    if (typeof ResizeObserver === 'function') { topbarObserver = new ResizeObserver(measure); topbarObserver.observe(topbar) }
  }
}
// The Diary's ⋯ beside + Book a visit (tidy-up item 4; Liam, 20 Oct 2026, his drawing top-after.jpg): each choice is a
// title with a line under it ("Jump to a date…", "Or back to today"), and a tap opens its own box under it (a date, or
// the question before a week moves), one box at a time. Day and Week (schedule.ts) and Month (month.ts) fill it.
export interface DiaryMoreItem { title: string; hint: string; className?: string; fill: (box: HTMLElement, close: () => void) => void }
export function diaryMoreMenu(label: string, items: DiaryMoreItem[]): HTMLElement {
  let shown: HTMLElement | null = null
  const fold = (): void => { if (shown) { shown.hidden = true; shown.previousElementSibling?.setAttribute('aria-expanded', 'false') } shown = null }
  const menu = rowMenu(label, (list, close) => {
    for (const item of items) {
      const box = el('div', { class: `diary-more-box${item.className ? ` ${item.className}` : ''}` })
      box.hidden = true
      const open = el('button', { type: 'button', class: 'diary-more-item', 'aria-expanded': 'false' }, el('strong', {}, item.title), el('span', {}, item.hint))
      open.addEventListener('click', () => {
        const again = shown === box
        fold()
        if (again) return
        shown = box; box.hidden = false; open.setAttribute('aria-expanded', 'true')
        item.fill(box, close)
      })
      list.append(open, box)
    }
  }) as HTMLDetailsElement
  menu.classList.add('diary-week-tools', 'diary-more')
  // Closed, it opens on its choices again next time.
  menu.addEventListener('toggle', () => { if (!menu.open) fold() })
  return menu
}
// The desktop arrangement of New diary (approved Opus design, 8 Oct 2026). A phone keeps the shipped New layout.
export function diaryDesk(): boolean {
  return !(typeof matchMedia === 'function' && matchMedia('(max-width: 760px)').matches)
}
// One day's figures for the desktop diary. Visits are actual booked or done work: cancelled rows, Roundly's
// suggestions and contact reminders are counted apart and never reach totals, estimates or progress. Done is
// not Paid, and an unknown length stays unknown rather than counting as nothing.
export interface DiaryDayFacts {
  visits: number; open: number; done: number; paid: number; owed: number; unrecorded: number
  agreed: number; pencilled: number; unassigned: number; cancelled: number; suggested: number
  pence: number; agreedPence: number; pencilledPence: number; paidPence: number; owedPence: number; unrecordedPence: number
  unpriced: boolean; minutes: number; missing: number
}
export function diaryDayFacts(rows: readonly Clean[], who: (c: Clean) => string = c => c.worker ?? ''): DiaryDayFacts {
  const real = rows.filter(c => !isContact(c))
  const visits = real.filter(countsAsDayVisit)
  const open = visits.filter(c => c.state === 'booked'), done = visits.filter(c => c.state === 'done')
  const sum = (list: readonly Clean[]): number => list.reduce((n, c) => n + (c.amount_pence ?? 0), 0)
  // Owed's split (docs/DIARY-AND-MESSAGES.md, Owed): not paid is what someone marked unpaid, counted at what is still to
  // pay when the read carries the money; nobody recording the payment is its own figure, as on Owed.
  const paid = done.filter(c => c.payment_status === 'paid'), owed = done.filter(c => c.payment_status === 'unpaid')
  const unrecorded = done.filter(c => c.payment_status !== 'paid' && c.payment_status !== 'unpaid')
  const toPay = (c: Clean): number => Math.max(0, (c.amount_pence ?? 0) - Math.max(0, c.from_credit_pence ?? 0) - Math.max(0, c.paid_in_pence ?? 0))
  const counts = diaryCounts(open)
  return {
    visits: visits.length, open: open.length, done: done.length, paid: paid.length, owed: owed.length, unrecorded: unrecorded.length,
    agreed: counts.agreed, pencilled: counts.pencilled, unassigned: open.filter(c => !who(c).trim()).length,
    cancelled: real.filter(c => c.state === 'cancelled').length, suggested: real.filter(c => c.state === 'booked' && isSuggestion(c)).length,
    pence: sum(visits), agreedPence: sum(open.filter(c => c.confirmed)), pencilledPence: sum(open.filter(c => !c.confirmed)),
    paidPence: sum(paid), owedPence: owed.reduce((n, c) => n + toPay(c), 0), unrecordedPence: sum(unrecorded), unpriced: visits.some(c => c.amount_pence == null),
    minutes: counts.minutes, missing: counts.missing,
  }
}
// "2h 30m", or the honest words when lengths are missing: never a short total presented as the whole day.
export function diaryEstimate(f: Pick<DiaryDayFacts, 'open' | 'minutes' | 'missing'>): { main: string; note: string } {
  if (!f.open) return { main: 'Nothing open', note: '' }
  if (f.missing === f.open) return { main: 'Time not estimated', note: `${f.missing} visit${f.missing === 1 ? ' has' : 's have'} no estimate` }
  return { main: `${Math.floor(f.minutes / 60)}h ${f.minutes % 60}m`, note: f.missing ? `plus ${f.missing} not estimated` : '' }
}
export function diaryCounts(rows: readonly Clean[]): { agreed: number; pencilled: number; done: number; suggested: number; minutes: number; missing: number } {
  const visits = rows.filter(countsAsDayVisit)
  const open = visits.filter(c => c.state === 'booked')
  return {
    agreed: open.filter(c => c.confirmed).length,
    pencilled: open.filter(c => isPencilledVisit(c)).length,
    done: visits.filter(c => c.state === 'done').length,
    suggested: rows.filter(isSuggestion).length,
    minutes: open.reduce((n,c) => n + (c.duration_minutes && c.duration_minutes > 0 ? c.duration_minutes : 0), 0),
    missing: open.filter(c => !c.duration_minutes || c.duration_minutes <= 0).length,
  }
}
export function countLine(rows: readonly Clean[], workload = true): HTMLElement {
  const c = diaryCounts(rows)
  const line = el('div', { class: 'diary-counts' })
  // "not agreed": every visit whose day is not agreed; "Pencilled" names the redesign's group (src/pencilled.ts).
  for (const [n,label] of [[c.agreed,'agreed'],[c.pencilled,'not agreed'],[c.done,'done'],[c.suggested,'suggested']] as const) {
    if (n) line.append(el('span', { class: `diary-count diary-count-${label}` }, `${n} ${label}`))
  }
  const open = rows.filter(v => countsAsDayVisit(v) && v.state === 'booked').length
  if (workload && open) line.append(el('span', { class: 'diary-workload' }, diaryEstimateLine(c.minutes,c.missing,open)))
  return line
}
export function diaryEstimateLine(minutes: number, missing: number, visits: number): string {
  if (!visits) return 'No open visits'
  if (missing === visits) return 'Time not estimated'
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m estimated${missing ? ` · ${missing} without estimates` : ''}`
}
export function diaryDayProgress(rows: readonly Clean[], filtered = false): HTMLElement {
  const visits = rows.filter(countsAsDayVisit)
  const done = visits.filter(c => c.state === 'done').length
  const label = visits.length ? `${done} of ${visits.length} done · ${visits.length - done} remaining` : filtered ? 'No matching visits' : 'No visits booked'
  const line = el('p', {class:'diary-day-progress',role:'status'}, `${label}${filtered ? ' · filtered' : ''}`)
  if (visits.length) line.append(el('progress', {max:String(visits.length),value:String(done),'aria-label':filtered ? 'Matching visits completed' : 'Visits completed'}))
  return line
}
export function diaryFeedback(node: HTMLElement | null, reverse = false): void {
  if (!node) return
  node.classList.add('diary-feedback')
  window.setTimeout(() => node.classList.remove('diary-feedback'), 1100)
  if (typeof matchMedia !== 'function' || matchMedia('(prefers-reduced-motion: reduce)').matches || !node.animate) return
  node.animate([{ opacity: .5, transform: `translateY(${reverse ? '-3' : '3'}px)` }, { opacity: 1, transform: 'translateY(0)' }], {duration: 180, easing:'ease-out'})
}

// Keep effects bounded to the node that owns them. A superseded close must
// never hide a panel reopened while its old animation was still finishing.
const motions = new WeakMap<HTMLElement, Animation>()
const panelChanges = new WeakMap<HTMLElement, object>()
export function diaryMotion(node: HTMLElement | null, frames: Keyframe[], duration: number): Animation | null {
  if (!node) return null
  motions.get(node)?.cancel(); motions.delete(node)
  if (!node.isConnected || typeof matchMedia !== 'function' || matchMedia('(prefers-reduced-motion: reduce)').matches || !node.animate) return null
  const effect = node.animate(frames, {duration, easing:'ease-out'})
  motions.set(node, effect)
  return effect
}
export function diaryPanel(node: HTMLElement, open: boolean, duration = 170): void {
  const token = {}; panelChanges.set(node, token)
  const wasHidden = node.hidden
  node.inert = !open
  node.setAttribute('aria-hidden', String(!open))
  if (wasHidden && !open) return
  node.hidden = false
  const height = node.getBoundingClientRect().height
  const effect = diaryMotion(node, open ? [{height:'0px',opacity:0},{height:`${height}px`,opacity:1}] : [{height:`${height}px`,opacity:1},{height:'0px',opacity:0}], duration)
  if (!effect) { node.hidden = !open; return }
  void effect.finished.then(() => { if (panelChanges.get(node) === token) node.hidden = !open }, () => {})
}
export interface DiaryOrderEffect { date: string; id: number; reverse: boolean; positions: Map<number, number> }
export function diaryOrderCapture(date: string, id: number, reverse: boolean): DiaryOrderEffect {
  const positions = new Map<number, number>()
  for (const row of document.querySelectorAll<HTMLElement>(`.day-card[data-date="${date}"] [data-clean-id]`)) positions.set(Number(row.dataset.cleanId), row.getBoundingClientRect().top)
  return {date,id,reverse,positions}
}
export function diaryOrderSettle(root: HTMLElement, effect: DiaryOrderEffect): void {
  for (const row of root.querySelectorAll<HTMLElement>(`.day-card[data-date="${effect.date}"] [data-clean-id]`)) {
    const before = effect.positions.get(Number(row.dataset.cleanId))
    const distance = before == null ? 0 : Math.max(-240, Math.min(240, before - row.getBoundingClientRect().top))
    if (distance) diaryMotion(row, [{transform:`translateY(${distance}px)`},{transform:'translateY(0)'}],250)
    if (Number(row.dataset.cleanId) === effect.id) {
      row.querySelector<HTMLButtonElement>('.diary-row-summary')?.focus({preventScroll:true})
      row.classList.add('diary-feedback')
      window.setTimeout(() => row.classList.remove('diary-feedback'),1100)
    }
  }
}
// Cross-day moves affect both days and, in Month, the calendar chips as well
// as the selected-day rows. Keys keep those two representations distinct.
export interface DiaryMoveEffect { id: number; dates: string[]; positions: Map<string, {top: number; left: number}> }
const moveNodes = (root: ParentNode, dates: string[]): HTMLElement[] => [...root.querySelectorAll<HTMLElement>(dates.flatMap(date => [`.day-card[data-date="${date}"] .clean-row[data-clean-id]`, `.day-card[data-date="${date}"] .month-row[data-clean-id]`, `.month-day[data-date="${date}"] .month-chip[data-clean-id]`]).join(','))]
const moveKey = (node: HTMLElement): string => `${node.classList.contains('month-chip') ? 'chip' : 'row'}:${node.dataset.cleanId}`
export function diaryMoveCapture(from: string, to: string, id: number): DiaryMoveEffect {
  const dates = [...new Set([from,to])], positions = new Map<string, {top:number; left:number}>()
  for (const node of moveNodes(document,dates)) {
    const rect = node.getBoundingClientRect()
    if (rect.height) positions.set(moveKey(node), {top:rect.top,left:rect.left})
  }
  return {id,dates,positions}
}
export function diaryMoveSettle(root: HTMLElement, effect: DiaryMoveEffect): void {
  for (const node of moveNodes(root,effect.dates)) {
    const moved = Number(node.dataset.cleanId) === effect.id
    const before = effect.positions.get(moveKey(node)) ?? (moved ? effect.positions.get(`row:${effect.id}`) ?? effect.positions.get(`chip:${effect.id}`) : undefined)
    if (before) {
      const rect = node.getBoundingClientRect()
      const x = Math.max(-240,Math.min(240,before.left-rect.left)), y = Math.max(-240,Math.min(240,before.top-rect.top))
      if (rect.height && (x || y)) diaryMotion(node,[{transform:`translate(${x}px,${y}px)`},{transform:'translate(0,0)'}],250)
    }
    if (moved) {
      node.classList.add('diary-feedback')
      window.setTimeout(() => node.classList.remove('diary-feedback'),1100)
      node.querySelector<HTMLButtonElement>('.diary-row-summary')?.focus({preventScroll:true})
    }
  }
}
export function diarySelectionTint(node: HTMLElement | null): void {
  if (node) diaryMotion(node,[{backgroundColor:'#fff0a6'},{backgroundColor:getComputedStyle(node).backgroundColor}],180)
}
let completionSettle: {id: number; top: number; who: string; root: Element | null; current: () => boolean} | null = null
export function diaryCompletion(rect: DOMRect, id: number, current: () => boolean): void {
  if (rect.bottom <= 0 || rect.top >= window.innerHeight) return
  completionSettle = {id,top:rect.top,who:diaryPreferenceKey('completion'),root:document.querySelector('#screen'),current}
  const mark = el('span', {class:'diary-completion-receipt', role:'status', 'aria-label':'Visit completed'}, el('span', {'aria-hidden':'true'}, '✓'))
  mark.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l4 4L19 6" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  mark.style.top = `${Math.max(8,Math.min(window.innerHeight - 40,rect.top + 12))}px`
  mark.style.left = `${Math.max(8,rect.left + 34)}px`
  document.body.append(mark)
  window.setTimeout(() => mark.remove(),800)
}

// The approved presentation shared by week cards and the month's selected day.
// Values use actual visits only; suggestions and contact reminders are not income.
export function diaryDayTotals(rows: readonly Clean[]): HTMLElement {
  const open = rows.filter(c => c.state === 'booked' && !isContact(c) && !isSuggestion(c))
  const box = el('div', {class:'diary-day-totals'})
  for (const [label, visits] of [['Agreed', open.filter(c => c.confirmed)], ['Not agreed', open.filter(c => !c.confirmed)]] as const) {
    if (!visits.length) continue
    const c = diaryCounts(visits)
    box.append(el('div', {class:'diary-total'},
      el('span', {}, `${label} · ${visits.length} visit${visits.length === 1 ? '' : 's'}`),
      el('strong', {}, money(visits.reduce((n,c) => n + (c.amount_pence ?? 0), 0)).replace(/\.00$/, '')),
      el('small', {}, `${diaryEstimateLine(c.minutes,c.missing,visits.length)}${visits.some(c => c.amount_pence == null) ? ' · plus unpriced work' : ''}`)))
  }
  return box
}
// A round with people to pay closes out its day on its own page (Close out the day, 29 Oct 2026), so its footer has
// Day notes only; a one-person round keeps its Day totals here.
export function diaryDayFooter(notes: HTMLElement, ledger: HTMLElement | null): HTMLElement {
  const footer = el('div', {class:'diary-day-footer'})
  for (const [label, content] of [['Day notes', notes], ...(ledger ? [['Day totals', ledger]] as const : [])] as const) {
    const fold = el('details', {class:'diary-day-tool'}, el('summary', {}, label), content)
    footer.append(fold)
  }
  return footer
}
export function diaryHeading(heading: HTMLElement): void {
  const title = heading.querySelector('h2')
  if (title) { title.textContent = 'Diary'; title.before(el('span', {class:'diary-eyebrow'}, 'YOUR WEEK, CLEARLY')) }
  const subtitle = heading.querySelector('p')
  if (subtitle) subtitle.textContent = 'Your round at a glance. Move jobs as plans change.'
}
